import type {
  CalendarEventsResult,
  EventsAiSummaryResult,
  GoogleCalendarsResult,
  GoogleLoginInput,
  GoogleLoginResult,
  ListAccessCodesResponse,
  AppApiClient,
  OnboardingProfile,
  SaveSearchCriteriaResult,
  SearchCriteria,
  SearchCriteriaInput,
  SignInInput,
  SignUpInput,
  SuggestedEventsResult,
  UserGoals,
  OutreachStats,
} from "@/shared/api/contracts";
import type {
  HeaderStatsResponse,
  NetworkHealthResponse,
  PriorityActionsResponse,
  PriorityActionWire,
  RoutesResponse,
} from "@/features/dashboard/contracts";
import type {
  ActiveSearchTarget,
  ActiveSearchTargetSuggestion,
  CreateActiveSearchTargetInput,
  UpdateActiveSearchTargetInput,
} from "@/features/job-tracker/activeSearchTargets";
import type {
  AddCompanyResponse,
  DismissResponse,
  PinResponse,
  ProposalDto,
  ProposalEvidence,
  ProposalsResponse,
  TargetCompanyDto,
} from "@/features/pathways/contracts";
import type {
  ExtensionInitiateResponse,
  ExtensionSessionResponse,
} from "@/features/extension/contracts";
import type {
  DiscoverCompaniesParams,
  DiscoverCompany,
  DiscoverOutreachDraft,
  DiscoverOutreachInput,
  DiscoverPerson,
  DiscoverPreferredCompany,
} from "@/features/discover/discoverContracts";
import type {
  CareerSearchProfile,
  TargetedRefreshResponse,
} from "@/features/job-tracker/contracts";
import { normalizeCompanyName } from "@/features/discover/discoverContracts";
import type {
  CompleteOnboardingInput,
  CompleteOnboardingResult,
  GenerateQuizResult,
  GetOnboardingDraftResult,
  LinkedInExtractionResult,
  OnboardingOptions,
  OnboardingStepStatus,
  OnboardingValidationIssue,
  ParseCvResult,
  PersonalizedQuestionResult,
  SaveOnboardingStepInput,
  SaveOnboardingStepResult,
} from "@/features/onboarding/contracts";
import { resolveMockScenario } from "@/shared/api/mockScenarios";
import { realApiClient } from "@/shared/api/real/realApiClient";
import { storeCompanyDomain } from "@/shared/ui/companyDomain";
import type { AppBootstrap, AppSession, UserProfile } from "@/shared/types/app";
import {
  onboardingStepOrder,
  type OnboardingAnswers,
  type OnboardingDraft,
  type OnboardingGoal,
  type OnboardingResume,
  type OnboardingStepId,
} from "@/features/onboarding";

const mockSession: AppSession = {
  expiresAt: null,
  user: {
    displayName: "Liminl Test",
    email: "test@goliminl.local",
    id: "dev-user",
  },
};

const storageKey = "liminl.devOnboardingDraft";
const mockDismissedActionStorageKey = "liminl.devDismissedDashboardActions";
const mockActiveTargetsStorageKey = "liminl.devActiveSearchTargets";
const mockDismissedTargetProposalStorageKey = "liminl.devDismissedTargetProposals";
const mockTargetCompaniesStorageKey = "liminl.devTargetCompanies";
const MOCK_MAX_TARGET_COMPANIES = 10;
const mockExtensionConnectedStorageKey = "liminl.mockExtensionConnected";
const mockGoalsStorageKey = "liminl.devGoals";
const mockSearchCriteriaStorageKey = "liminl.devSearchCriteria";
const mockTargetedRefreshes = new Map<string, TargetedRefreshResponse>();

function timeframeToMockTimeline(
  timeframe: OnboardingGoal["timeframe"] | undefined,
): UserGoals["timeline"] {
  switch (timeframe) {
    case "now":
      return "asap";
    case "one_to_three_months":
      return "3mo";
    case "three_to_six_months":
      return "6mo";
    case "six_months_plus":
      return "12mo";
    case "exploratory":
      return "exploring";
    default:
      return "3mo";
  }
}

function workModeToMockSetup(workMode: OnboardingGoal["workMode"] | undefined): UserGoals["work_setup"] {
  switch (workMode) {
    case "remote":
      return "remote";
    case "hybrid":
      return "hybrid";
    case "onsite":
      return "office";
    case "open":
      return "any";
    default:
      return "any";
  }
}

function readMockSearchCriteria(): SearchCriteria | null {
  return readJson<SearchCriteria | null>(mockSearchCriteriaStorageKey, null);
}

function writeMockSearchCriteria(criteria: SearchCriteria | null) {
  writeJson(mockSearchCriteriaStorageKey, criteria);
}

function searchCriteriaFromGoal(goal: OnboardingGoal | null | undefined): SearchCriteria | null {
  if (!goal) return null;
  const roleLabel =
    goal.targets.find((t) => t.kind === "role")?.roleTitle ??
    goal.targets.find((t) => t.kind === "role")?.label ??
    goal.headline;
  if (!roleLabel?.trim()) return null;
  const arrangements =
    goal.workMode === "open"
      ? (["onsite", "hybrid", "remote"] as const)
      : goal.workMode === "remote"
        ? (["remote"] as const)
        : goal.workMode === "hybrid"
          ? (["hybrid"] as const)
          : (["onsite"] as const);
  return {
    id: "mock-search-criteria",
    criteriaVersion: 1,
    role: roleLabel.trim(),
    sectors: goal.targets
      .filter((t) => t.kind === "industry")
      .map((t) => t.industryName ?? t.label),
    geo: goal.locations[0] ?? "",
    seniorityBand: goal.seniorityBand ?? "MID",
    jobTrackerPreferences: {
      roleAliases: [],
      salaryMin: goal.salaryMin ?? null,
      salaryMax: goal.salaryMax ?? null,
      workArrangements: [...arrangements],
      employmentTypes: ["full_time"],
    },
  };
}

function mockSearchProfile(): CareerSearchProfile {
  return {
    version: 1,
    inputFingerprint: "dev-profile",
    generatedAt: new Date().toISOString(),
    generation: "llm",
    explicit: {
      targetRoles: ["Product Operations"],
      roleAliases: [],
      industries: ["Climate tech"],
      locations: ["New York"],
      targetCompanies: [],
      workArrangements: ["hybrid"],
      employmentTypes: ["full_time"],
      statedSalaryMin: 120000,
      statedSalaryMax: 160000,
      statedSeniority: "MID",
      inferenceExclusions: {
        roleFamilies: [],
        adjacentTitles: [],
        industries: [],
        companyArchetypes: [],
        adjacentCompanies: [],
      },
    },
    resume: { titles: ["Product Operator"], skills: ["Product operations"], industries: [], currentSeniority: "MID" },
    inferred: {
      roleFamilies: [{ value: "Business Operations", reason: "Based on your product operations background.", confidence: 0.86, provenance: "llm" }],
      adjacentTitles: [{ value: "Strategy & Operations Manager", reason: "Based on your target role and experience.", confidence: 0.8, provenance: "llm" }],
      industries: [],
      companyArchetypes: [{ value: "Climate software", reason: "Based on your stated climate-tech interest.", confidence: 0.84, provenance: "llm" }],
      adjacentCompanies: [],
    },
    effective: {
      targetRoles: ["Product Operations", "Business Operations", "Strategy & Operations Manager"],
      industries: ["Climate tech"],
      locations: ["New York"],
      companies: [],
      targetSeniority: "MID",
      salaryFloor: 108000,
      salaryMaxDisplay: 160000,
      workArrangements: ["hybrid"],
      employmentTypes: ["full_time"],
      skills: ["Product operations"],
    },
  };
}

function mockOnboardingStatus(): UserProfile["onboardingStatus"] {
  if (resolveMockScenario() !== "onboarding_incomplete") return "complete";
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw && (JSON.parse(raw) as OnboardingDraft).status === "complete") return "complete";
  } catch {
    // best effort
  }
  return "not_started";
}

const mockProfile = (): UserProfile => ({
  displayName: mockSession.user.displayName,
  id: mockSession.user.id,
  onboardingStatus: mockOnboardingStatus(),
  hasSubmittedReferences: mockOnboardingStatus() === "complete",
});

function mockAccessCode(): ListAccessCodesResponse {
  return {
    code: {
      code: "liminl-test",
      createdAt: new Date().toISOString(),
      exhaustedAt: null,
      maxUses: 3,
      revokedAt: null,
      revokedReason: null,
      status: "ACTIVE",
      usesRemaining: 3,
    },
  };
}

const relationshipComfort = {
  allowDraftGeneration: true,
  boundaries: [
    { enabled: true, id: "no-cold-dms", label: "Do not suggest cold DMs without context" },
    { enabled: true, id: "draft-first", label: "Show me drafts before anything is sent" },
    { enabled: true, id: "protect-close-ties", label: "Be careful with close personal relationships" },
  ],
  introPreference: "ask_for_context",
  notes: "",
  outreachComfort: "known_contacts_first",
} satisfies OnboardingAnswers["relationshipComfort"];

const mockOptions: OnboardingOptions = {
  acceptedResumeTypes: [".pdf", ".txt"],
  defaultRelationshipComfort: relationshipComfort,
  networkSourceKinds: ["google", "linkedin", "csv_import", "manual_contacts"],
  relationshipBoundaryTemplates: relationshipComfort.boundaries.map(({ id, label }) => ({ id, label })),
  suggestedCompanies: ["Watershed", "Stripe", "Notion", "Figma", "Anthropic"],
  suggestedDomains: ["B2B SaaS", "Developer tools", "Sustainability", "Student programs"],
  suggestedIndustries: ["Climate tech", "AI tools", "Healthcare", "Education", "Fintech"],
  suggestedRoles: [
    "Product Manager",
    "Product Operations",
    "Software Engineer",
    "Design Program Manager",
    "Chief of Staff",
  ],
};

const emptyAnswers = (): OnboardingAnswers => ({
  goal: null,
  manualContacts: [],
  networkSources: [],
  relationshipComfort,
  resume: null,
  review: null,
  searchIntent: null,
  careerQuiz: null,
  phone: null,
});

const mockResume = (): OnboardingResume => ({
  fileName: "Liminl-test-resume.pdf",
  fileSizeLabel: "184 KB",
  parsedSignals: [
    { id: "role", label: "Role signal", value: "Product operations and go-to-market systems" },
    { id: "industry", label: "Industry signal", value: "Climate tech, AI tools, and marketplaces" },
    { id: "strength", label: "Strength", value: "Relationship-driven execution" },
  ],
  parsedCv: mockParseCvResult().extracted,
  source: "resume_upload",
  status: "parsed",
  uploadedAt: new Date().toISOString(),
});

const mockParseCvResult = (): ParseCvResult => ({
  extracted: {
    email: mockSession.user.email,
    experiences: [{ company: "Liminl Labs", dates: "2024-present", role: "Product operator" }],
    focusAreas: ["Climate tech", "AI tools"],
    fullName: mockSession.user.displayName,
    graduationYear: "",
    location: "New York",
    major: "",
    minor: "",
    skills: ["Product operations", "Relationship mapping", "Go-to-market"],
    suggestedTargetIndustries: ["Climate tech", "AI tools"],
    suggestedTargetRoles: ["Product Operations", "Chief of Staff"],
    summary: "Local mock resume context for UI review.",
    university: "",
    values: ["High agency", "Warm relationships", "Clear communication"],
  },
});

const mockGoal = (): OnboardingGoal => ({
  headline: "Explore product operations roles in climate tech",
  kind: "find_next_role",
  locations: ["New York", "Remote"],
  notes: "",
  searchTrack: "full_time",
  targetOutcome: "Find warm routes into teams where product, operations, and climate intersect.",
  targets: [
    {
      id: "target-role-product-ops",
      kind: "role",
      label: "Product Operations",
      priority: "primary",
      roleTitle: "Product Operations",
      tags: [],
    },
    {
      companyName: "Watershed",
      id: "target-company-watershed",
      kind: "company",
      label: "Watershed",
      priority: "secondary",
      tags: [],
    },
  ],
  timeframe: "one_to_three_months",
  workMode: "open",
});

function initialAnswers(): OnboardingAnswers {
  if (resolveMockScenario() === "onboarding_incomplete") return emptyAnswers();

  return {
    ...emptyAnswers(),
    goal: mockGoal(),
    manualContacts: [
      {
        company: "Watershed",
        email: "mara@example.com",
        fullName: "Mara Kline",
        id: "manual-mara",
        relationship: "mentor",
        role: "Head of People",
      },
    ],
    networkSources: [
      {
        actionStatus: "added",
        addedCount: 1,
        connected: false,
        intent: "connect_now",
        kind: "manual_contacts",
        label: "Manual contacts",
        permissions: ["contacts"],
      },
    ],
    resume: mockResume(),
  };
}

function readDraft(): OnboardingDraft {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw) return JSON.parse(raw) as OnboardingDraft;
  } catch {
    // best effort
  }

  return makeDraft(initialAnswers(), 1);
}

function writeDraft(draft: OnboardingDraft) {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(draft));
  } catch {
    // best effort
  }
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson<T>(key: string, value: T) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // best effort
  }
}

function mockGoals(): UserGoals {
  const draft = readDraft();
  const goal = draft.answers.goal ?? mockGoal();
  const roleTargets = goal.targets.filter((target) => target.kind === "role").map((target) => target.label);
  const companyTargets = goal.targets.filter((target) => target.kind === "company").map((target) => target.label);
  const industryTargets = goal.targets.filter((target) => target.kind === "industry").map((target) => target.label);
  const domainTargets = goal.targets.filter((target) => target.kind === "domain").map((target) => target.label);
  const focusAreas = Array.from(
    new Set([...(goal.focusAreas ?? []).map((area) => area.trim()).filter(Boolean), ...domainTargets]),
  );

  return {
    focus_areas: focusAreas.length > 0 ? focusAreas : ["Climate tech", "AI tools"],
    goal_brief: goal.headline,
    goal_kind: goal.kind,
    location: goal.locations[0] ?? "New York",
    locations: goal.locations,
    looking_for: goal.searchTrack === "internship" ? "internship" : "fulltime",
    target_companies: companyTargets.length > 0 ? companyTargets : ["Watershed", "Stripe"],
    target_industries: industryTargets.length > 0 ? industryTargets : ["Climate tech"],
    target_outcome: goal.targetOutcome,
    target_role: roleTargets[0] ?? "Product Operations",
    target_roles: roleTargets.length > 0 ? roleTargets : ["Product Operations", "Chief of Staff"],
    timeline: timeframeToMockTimeline(goal.timeframe),
    work_setup: workModeToMockSetup(goal.workMode),
    searchIntent: draft.answers.searchIntent ?? "active",
    salary_expectation: {
      min: goal.salaryMin ?? 90000,
      max: goal.salaryMax ?? 130000,
      currency: "USD",
      period: "year",
    },
  };
}

function mockOnboardingProfile(): OnboardingProfile {
  const parsedCv = readDraft().answers.resume?.parsedCv;
  const draft = readDraft();

  return {
    address: parsedCv?.location ?? "New York",
    cvFileName: draft.answers.resume?.fileName ?? "Nicki_Jackson_Resume.pdf",
    experienceSummary:
      parsedCv?.summary ??
      "Product operator focused on climate and AI tools. Previously at early-stage startups.",
    fullName: parsedCv?.fullName ?? mockSession.user.displayName,
    graduationYear: parsedCv?.graduationYear || null,
    major: parsedCv?.major || null,
    minor: parsedCv?.minor || null,
    university: parsedCv?.university || null,
  };
}

function mockHeaderStats(): HeaderStatsResponse {
  if (resolveMockScenario() === "no_priority_actions" || resolveMockScenario() === "empty_network") {
    return { actionsThisWeek: 0, introsMade: 0, interviews: 0 };
  }

  return { actionsThisWeek: 5, introsMade: 2, interviews: 1 };
}

function mockNetworkHealth(): NetworkHealthResponse {
  if (resolveMockScenario() === "empty_network") {
    return {
      breakdown: { consistency: 0, freshness: 0, mix: 0, totalContacts: 0, trend: 42 },
      label: "Add a network source so Liminl can start ranking routes.",
      score: 24,
      tier1Count: 0,
      tier2Count: 0,
      tier3Count: 0,
      tier4Count: 0,
      totalContacts: 0,
    };
  }

  return {
    breakdown: { consistency: 74, freshness: 68, mix: 63, totalContacts: 85, trend: 71 },
    label: "Warm enough to rank routes. Keep close ties fresh before broader outreach.",
    score: 78,
    tier1Count: 8,
    tier2Count: 15,
    tier3Count: 28,
    tier4Count: 34,
    totalContacts: 85,
  };
}

const basePriorityActions: PriorityActionWire[] = [
  {
    actionKey: "review-mara-watershed-route",
    category: "warm_intro",
    urgency: "HIGH",
    minutes: 3,
    title: "Ask Mara Kline about Watershed",
    reason: "Mara is the warmest first hop into Watershed, so start here before broader outreach.",
    targetCompany: "Watershed",
  },
  {
    actionKey: "follow-up-jon-product-ops",
    category: "follow_up",
    urgency: "HIGH",
    minutes: 2,
    title: "Follow up with Jon Taylor on product ops timing",
    reason: "Jon can point you toward the right product operations team, and the thread is warm enough to move now.",
    targetCompany: "Watershed",
  },
  {
    actionKey: "connect-gmail-context",
    actionType: "connect_gmail",
    category: "strategic_move",
    urgency: "MEDIUM",
    minutes: 2,
    title: "Connect Google Calendar & Contacts to map who you already know",
    reason: "Calendar and contacts context helps Liminl rank follow-ups and surface warm paths.",
  },
  {
    actionKey: "review-sourced-role",
    category: "research",
    urgency: "MEDIUM",
    minutes: 5,
    title: "Review a Product Operations role at Watershed",
    reason: "A sourced role matches your target, but fit and seniority need a quick read before you invest time.",
    opportunityId: "mock-watershed-product-ops",
    targetCompany: "Watershed",
  },
  {
    actionKey: "confirm-priya-signal",
    category: "data_hygiene",
    urgency: "MEDIUM",
    minutes: 4,
    title: "Confirm whether Priya Lee still knows climate operators",
    reason: "This route could help, but Liminl needs one fresh note before ranking it higher.",
  },
  {
    actionKey: "reconnect-rachel-stripe",
    category: "reconnect",
    urgency: "LOW",
    minutes: 4,
    title: "Reconnect with Rachel Green before asking about Stripe",
    reason: "Rachel is close to a target company, but the relationship needs a light refresh first.",
    targetCompany: "Stripe",
  },
];

const dashboardReviewActionFloor = 4;

function isSetupPriorityAction(action: PriorityActionWire) {
  return action.actionType === "connect_gmail" || action.actionType === "add_contacts";
}

function dismissedActionKeys(): string[] {
  return readJson<string[]>(mockDismissedActionStorageKey, []);
}

function mockPriorityActions(): PriorityActionsResponse {
  if (resolveMockScenario() === "no_priority_actions" || resolveMockScenario() === "empty_network") {
    return { actions: [] };
  }

  const dismissed = new Set(dismissedActionKeys());
  const visibleActions = basePriorityActions.filter((action) => !dismissed.has(action.actionKey));
  const visibleReviewActions = visibleActions.filter((action) => !isSetupPriorityAction(action));

  // Keep the default happy-path dashboard visually representative even if a
  // prior local session dismissed most of the actionable rows and left only
  // setup prompts behind. Empty/no-actions reviews should use the dedicated
  // mock scenarios instead of local dismissal residue.
  return {
    actions:
      visibleReviewActions.length >= dashboardReviewActionFloor
        ? visibleActions
        : basePriorityActions,
  };
}

function mockRoutes(): RoutesResponse {
  if (resolveMockScenario() === "empty_network") return { routes: [] };

  return {
    routes: [
      { company: "Watershed", id: "route-mara-watershed", strength: "Strong", via: "Mara Kline" },
      { company: "Stripe", id: "route-rachel-stripe", strength: "Medium", via: "Rachel Green" },
      { company: "Notion", id: "route-avery-notion", strength: "Weak", via: "Avery Liu" },
    ],
  };
}

function defaultActiveTargets(): ActiveSearchTarget[] {
  const now = new Date(0).toISOString();
  return [
    {
      archivedAt: null,
      companyName: "Watershed",
      createdAt: now,
      id: "mock-target-watershed",
      priority: 1,
      roleHint: "Product Operations",
      updatedAt: now,
      userId: mockSession.user.id,
    },
  ];
}

function readActiveTargets(): ActiveSearchTarget[] {
  return readJson<ActiveSearchTarget[]>(mockActiveTargetsStorageKey, defaultActiveTargets());
}

function writeActiveTargets(targets: ActiveSearchTarget[]) {
  writeJson(mockActiveTargetsStorageKey, targets);
}

function mockTargetSuggestions(): ActiveSearchTargetSuggestion[] {
  if (resolveMockScenario() === "empty_network") return [];

  const activeCompanies = new Set(
    readActiveTargets()
      .filter((target) => !target.archivedAt)
      .map((target) => target.companyName.toLowerCase()),
  );

  return [
    { companyName: "Stripe", contactCount: 4 },
    { companyName: "Notion", contactCount: 3 },
    { companyName: "Figma", contactCount: 2 },
  ].filter((suggestion) => !activeCompanies.has(suggestion.companyName.toLowerCase()));
}

function slugCompany(companyName: string): string {
  return companyName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function ensureMockActiveTarget(
  companyName: string,
  input: Pick<CreateActiveSearchTargetInput, "companyDomain" | "priority" | "roleHint"> = {},
): ActiveSearchTarget {
  const targets = readActiveTargets();
  const existing = targets.find(
    (target) => !target.archivedAt && target.companyName.toLowerCase() === companyName.toLowerCase(),
  );
  if (existing) return existing;

  const now = new Date().toISOString();
  const nextTarget: ActiveSearchTarget = {
    archivedAt: null,
    companyDomain: input.companyDomain ?? null,
    companyName,
    createdAt: now,
    id: `mock-target-${slugCompany(companyName)}`,
    priority: input.priority ?? targets.filter((target) => !target.archivedAt).length + 1,
    roleHint: input.roleHint ?? null,
    updatedAt: now,
    userId: mockSession.user.id,
  };
  writeActiveTargets([...targets, nextTarget]);
  return nextTarget;
}

function dismissedTargetProposalIds(): string[] {
  return readJson<string[]>(mockDismissedTargetProposalStorageKey, []);
}

/** Persistent (localStorage) Target Companies list for the dev-mock client -
 *  seeded once from mockGoals().target_companies, then independently
 *  add/removable so Settings' editor and Pathways "+Add" behave consistently. */
function readMockTargetCompanies(): TargetCompanyDto[] {
  const seeded = readJson<TargetCompanyDto[] | null>(mockTargetCompaniesStorageKey, null);
  if (seeded) return seeded;
  const initial = (mockGoals().target_companies ?? []).map((companyName, i) => ({
    id: `mock-target-${i}`,
    companyName,
  }));
  writeJson(mockTargetCompaniesStorageKey, initial);
  return initial;
}

function writeMockTargetCompanies(list: TargetCompanyDto[]): void {
  writeJson(mockTargetCompaniesStorageKey, list);
}

function mockTargetProposalForSuggestion(
  suggestion: ActiveSearchTargetSuggestion,
  index: number,
): ProposalDto {
  const companySlug = slugCompany(suggestion.companyName);
  const viaName =
    suggestion.companyName === "Stripe"
      ? "Rachel Green"
      : suggestion.companyName === "Notion"
        ? "Noor Patel"
        : "Avery Liu";
  const reachable = suggestion.contactCount > 0;
  const evidence: ProposalEvidence =
    index === 0 ? "reachable-role-match" : index === 1 ? "contact-role-match" : "enrichment";

  return {
    targetId: `mock-proposal-${companySlug}`,
    companyName: suggestion.companyName,
    confidence: index === 0 ? "HIGH" : "MEDIUM",
    evidence,
    roleFit: Math.max(0.62, 0.88 - index * 0.08),
    reachability: {
      reachable,
      bestPathDepth: reachable ? 2 : null,
      viaChain: reachable
        ? [
            {
              depth: 1,
              name: viaName,
              relationshipLabel: "Warm route",
              role: `Contact at ${suggestion.companyName}`,
            },
          ]
        : [],
      unlockHint: reachable
        ? null
        : `Add one credible contact around ${suggestion.companyName} before applying cold.`,
    },
    hasLiveOpening: index < 2,
  };
}

function mockTargetProposals(): ProposalDto[] {
  const dismissed = new Set(dismissedTargetProposalIds());
  return mockTargetSuggestions()
    .map(mockTargetProposalForSuggestion)
    .filter((proposal) => !dismissed.has(proposal.targetId));
}

function mockProposalsResponse(): ProposalsResponse {
  const proposals = mockTargetProposals();
  const reachableCount = proposals.filter((proposal) => proposal.reachability.reachable).length;
  return {
    proposals,
    skippedReason: null,
    summary: {
      total: proposals.length,
      reachableCount,
      noPathCount: proposals.length - reachableCount,
      criteriaVersion: 1,
    },
  };
}

const issue = (
  field: string,
  message: string,
  severity: OnboardingValidationIssue["severity"] = "error",
): OnboardingValidationIssue => ({ field, message, severity });

function validateStep(answers: OnboardingAnswers, stepId: OnboardingStepId): OnboardingValidationIssue[] {
  switch (stepId) {
    case "resume":
      return answers.resume ? [] : [issue("resume", "Add a resume or CV to continue.")];
    case "network":
      return answers.networkSources.length > 0 || answers.manualContacts.length > 0
        ? []
        : [issue("networkSources", "Choose at least one way to add your network.", "info")];
    case "goal":
      if (!answers.goal) return [issue("goal", "Tell Liminl what to optimize for.")];
      return [
        answers.goal.headline.trim() ? null : issue("headline", "Add a short goal headline."),
        answers.goal.targetOutcome.trim()
          ? null
          : issue("targetOutcome", "Describe the outcome Liminl should optimize for."),
        answers.goal.kind !== "liminl_decide" && answers.goal.targets.length === 0
          ? issue("targets", "Add at least one role, company, industry, or domain.")
          : null,
      ].filter(Boolean) as OnboardingValidationIssue[];
    case "review":
      return [];
  }
}

function completedSteps(answers: OnboardingAnswers): OnboardingStepId[] {
  const steps: OnboardingStepId[] = [];
  if (answers.resume) steps.push("resume");
  if (answers.networkSources.length > 0 || answers.manualContacts.length > 0) steps.push("network");
  if (answers.goal) steps.push("goal");
  return steps;
}

function makeDraft(answers: OnboardingAnswers, revision: number): OnboardingDraft {
  const completed = completedSteps(answers);
  const currentStep = onboardingStepOrder.find((step) => !completed.includes(step)) ?? "review";

  return {
    answers,
    completedSteps: completed,
    createdAt: new Date(0).toISOString(),
    currentStep,
    id: "dev-onboarding-draft",
    revision,
    status: completed.length === 0 ? "not_started" : "in_progress",
    updatedAt: new Date().toISOString(),
    userId: mockSession.user.id,
  };
}

function stepStatuses(draft: OnboardingDraft): OnboardingStepStatus[] {
  return onboardingStepOrder.map((stepId) => {
    const issues = validateStep(draft.answers, stepId);
    const hasError = issues.some((item) => item.severity === "error");
    const isEmpty =
      (stepId === "resume" && !draft.answers.resume) ||
      (stepId === "network" && draft.answers.networkSources.length === 0 && draft.answers.manualContacts.length === 0) ||
      (stepId === "goal" && !draft.answers.goal) ||
      (stepId === "review" && !draft.answers.review);

    return {
      issues,
      state: isEmpty ? "empty" : hasError ? "invalid" : draft.completedSteps.includes(stepId) ? "saved" : "valid",
      stepId,
    };
  });
}

function saveAnswers(answers: OnboardingAnswers, revision: number): SaveOnboardingStepResult {
  const draft = makeDraft(answers, revision);
  writeDraft(draft);
  return {
    draft,
    savedAt: draft.updatedAt,
    stepStatuses: stepStatuses(draft),
  };
}

// --- Mock Discover ("Companies" mode) state ---------------------------------
// In-memory only; resets on reload. The real feature persists via the backend
// Discover endpoints - this mock just keeps dev-mock scenarios functional and
// satisfies the AppApiClient contract.
const mockDiscoverPreferred: DiscoverPreferredCompany[] = [
  { id: "pref-stripe", name: "Stripe", normalizedName: normalizeCompanyName("Stripe") },
];
let mockDiscoverPreferredSeq = 0;

const mockDiscoverBaseCompanies: Array<Omit<DiscoverCompany, "preferred">> = [
  { key: "stripe", name: "Stripe", contactCount: 4 },
  { key: "watershed", name: "Watershed", contactCount: 2 },
  { key: "figma", name: "Figma", contactCount: 1 },
  { key: "notion", name: "Notion", contactCount: 0 },
];

const mockDiscoverPeopleByKey: Record<string, DiscoverPerson[]> = {
  stripe: [
    {
      id: "p-1",
      name: "Priya Raman",
      title: "Product Lead",
      company: "Stripe",
      relationshipStrength: 82,
      linkedInUrl: "https://www.linkedin.com/in/priya-raman",
      email: "priya@stripe.com",
      lastActivityAt: new Date().toISOString(),
      sources: ["LINKEDIN_CSV"],
    },
    {
      id: "p-2",
      name: "Marcus Lee",
      title: "Engineering Manager",
      company: "Stripe",
      relationshipStrength: 45,
      linkedInUrl: null,
      email: "marcus@stripe.com",
      lastActivityAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      sources: ["GOOGLE_PEOPLE"],
    },
  ],
  watershed: [
    {
      id: "p-3",
      name: "Dana Ortiz",
      title: "Climate Solutions",
      company: "Watershed",
      relationshipStrength: 0,
      linkedInUrl: "https://www.linkedin.com/in/dana-ortiz",
      email: null,
      lastActivityAt: null,
      sources: ["LINKEDIN_CSV"],
    },
  ],
};

function mockDiscoverIsPreferred(name: string): boolean {
  const normalized = normalizeCompanyName(name);
  return mockDiscoverPreferred.some((row) => row.normalizedName === normalized);
}

export const devMockApiClient: AppApiClient = {
  ...realApiClient,

  discover: {
    async listCompanies({ view, search, sort }: DiscoverCompaniesParams): Promise<DiscoverCompany[]> {
      let companies = mockDiscoverBaseCompanies.map((company) => ({
        ...company,
        preferred: mockDiscoverIsPreferred(company.name),
      }));
      if (view === "preferred") {
        companies = companies.filter((company) => company.preferred);
      }
      const needle = search?.trim().toLowerCase();
      if (needle) {
        companies = companies.filter((company) => company.name.toLowerCase().includes(needle));
      }
      // Mirror the backend's sort: name → A-Z, otherwise by contact count desc.
      companies.sort((a, b) =>
        sort === "name" ? a.name.localeCompare(b.name) : b.contactCount - a.contactCount,
      );
      return companies;
    },

    async listPeople(companyKey: string): Promise<DiscoverPerson[]> {
      return mockDiscoverPeopleByKey[companyKey] ?? [];
    },

    async listPreferredCompanies(): Promise<DiscoverPreferredCompany[]> {
      return [...mockDiscoverPreferred];
    },

    async addPreferredCompany(name: string): Promise<DiscoverPreferredCompany> {
      const normalizedName = normalizeCompanyName(name);
      const existing = mockDiscoverPreferred.find((row) => row.normalizedName === normalizedName);
      if (existing) return existing;
      mockDiscoverPreferredSeq += 1;
      const row: DiscoverPreferredCompany = {
        id: `pref-${mockDiscoverPreferredSeq}`,
        name,
        normalizedName,
      };
      mockDiscoverPreferred.push(row);
      return row;
    },

    async removePreferredCompany(id: string): Promise<void> {
      const index = mockDiscoverPreferred.findIndex((row) => row.id === id);
      if (index >= 0) mockDiscoverPreferred.splice(index, 1);
    },

    async createOutreachDraft(
      companyKey: string,
      input: DiscoverOutreachInput,
    ): Promise<DiscoverOutreachDraft> {
      const intent = input.intent?.trim() || "a quick intro chat";
      return {
        email: {
          subject: `Reaching out about ${companyKey}`,
          body: `Hi there,\n\nI'm exploring ${companyKey} and wanted to connect about ${intent}.\n\nBest,\nYou`,
        },
        linkedInDm: `Hi - I'm looking into ${companyKey} and would love to connect about ${intent}.`,
        contextSummary: `Mock draft for ${companyKey}${input.contactId ? ` · contact ${input.contactId}` : ""} · channel ${input.channel ?? "both"}.`,
      };
    },
  },

  jobs: {
    ...realApiClient.jobs,
    async getSearchProfile() {
      return mockSearchProfile();
    },
    async rebuildSearchProfile() {
      return mockSearchProfile();
    },
    async startTargetedRefresh() {
      const refreshId = `mock-targeted-${Date.now()}`;
      const response: TargetedRefreshResponse = {
        refreshId,
        status: "QUEUED",
        stage: "QUEUED",
      };
      mockTargetedRefreshes.set(refreshId, response);
      return response;
    },
    async getTargetedRefreshStatus(refreshId: string) {
      const current = mockTargetedRefreshes.get(refreshId);
      if (!current) throw new Error("Targeted refresh not found.");
      const completed: TargetedRefreshResponse = {
        ...current,
        status: "SUCCEEDED",
        stage: "COMPLETE",
        startedAt: current.startedAt ?? new Date().toISOString(),
        completedAt: new Date().toISOString(),
        result: {
          serpapi: {
            queries: 1,
            pages: 1,
            credits: 1,
            found: 3,
            qualified: 3,
            created: 3,
            refreshed: 0,
            errors: [],
          },
          ats: { found: 0 },
        },
      };
      mockTargetedRefreshes.set(refreshId, completed);
      return completed;
    },
  },

  dashboard: {
    async getHeaderStats(): Promise<HeaderStatsResponse> {
      if (resolveMockScenario() === "failed_api") throw new Error("Mock dashboard failure.");
      return mockHeaderStats();
    },

    async getNetworkHealth(): Promise<NetworkHealthResponse> {
      if (resolveMockScenario() === "failed_api") throw new Error("Mock dashboard failure.");
      return mockNetworkHealth();
    },

    async getPriorityActions(): Promise<PriorityActionsResponse> {
      if (resolveMockScenario() === "failed_api") throw new Error("Mock dashboard failure.");
      return mockPriorityActions();
    },

    async getRoutes(): Promise<RoutesResponse> {
      if (resolveMockScenario() === "failed_api") throw new Error("Mock dashboard failure.");
      return mockRoutes();
    },

    async dismissPriorityAction(actionKey: string): Promise<{ ok: true }> {
      const next = Array.from(new Set([...dismissedActionKeys(), actionKey]));
      writeJson(mockDismissedActionStorageKey, next);
      return { ok: true };
    },

    async completePriorityAction(actionKey: string): Promise<{ ok: true }> {
      const next = Array.from(new Set([...dismissedActionKeys(), actionKey]));
      writeJson(mockDismissedActionStorageKey, next);
      return { ok: true };
    },
  },

  pathways: {
    ...realApiClient.pathways,

    async getProposals(): Promise<ProposalsResponse> {
      return mockProposalsResponse();
    },

    async refreshProposals(): Promise<ProposalsResponse> {
      return mockProposalsResponse();
    },

    async addCompany(companyName: string): Promise<AddCompanyResponse> {
      const trimmed = companyName.trim();
      const list = readMockTargetCompanies();
      const existing = list.find((c) => c.companyName.toLowerCase() === trimmed.toLowerCase());
      if (existing) {
        return { targetId: existing.id, pinned: true, status: "ACTIVE", source: "USER_PINNED", created: false };
      }
      if (list.length >= MOCK_MAX_TARGET_COMPANIES) {
        throw new Error(`You can have at most ${MOCK_MAX_TARGET_COMPANIES} target companies. Remove one to add another.`);
      }
      const created = { id: `mock-target-${Date.now()}`, companyName: trimmed };
      writeMockTargetCompanies([...list, created]);
      return { targetId: created.id, pinned: true, status: "ACTIVE", source: "USER_PINNED", created: true };
    },

    async removeCompany(targetId: string): Promise<PinResponse> {
      const list = readMockTargetCompanies();
      writeMockTargetCompanies(list.filter((c) => c.id !== targetId));
      return { targetId, pinned: false, status: "DEAD", source: "USER_PINNED" };
    },

    async getTargetCompanies(): Promise<TargetCompanyDto[]> {
      return readMockTargetCompanies();
    },

    async pinProposal(id: string): Promise<PinResponse> {
      const proposal = mockTargetProposals().find((item) => item.targetId === id);
      if (proposal) {
        ensureMockActiveTarget(proposal.companyName, { roleHint: "Product Operations" });
      }
      return {
        targetId: id,
        pinned: true,
        status: "ACTIVE",
        source: "ROLE_SOURCED",
      };
    },

    async dismissProposal(id: string): Promise<DismissResponse> {
      const next = Array.from(new Set([...dismissedTargetProposalIds(), id]));
      writeJson(mockDismissedTargetProposalStorageKey, next);
      return {
        targetId: id,
        dismissed: true,
        criteriaVersion: 1,
      };
    },
  },

  activeSearchTargets: {
    async list(): Promise<ActiveSearchTarget[]> {
      return readActiveTargets().filter((target) => !target.archivedAt);
    },

    async create(input: CreateActiveSearchTargetInput): Promise<ActiveSearchTarget> {
      const target = ensureMockActiveTarget(input.companyName, input);
      if (input.companyDomain) {
        storeCompanyDomain(input.companyName, input.companyDomain);
      }
      return target;
    },

    async update(id: string, input: UpdateActiveSearchTargetInput): Promise<ActiveSearchTarget> {
      const targets = readActiveTargets();
      const target = targets.find((item) => item.id === id);
      if (!target) throw new Error(`Mock active target not found: ${id}`);

      const updated: ActiveSearchTarget = {
        ...target,
        priority: input.priority ?? target.priority,
        roleHint: input.roleHint ?? target.roleHint,
        updatedAt: new Date().toISOString(),
      };
      writeActiveTargets(targets.map((item) => (item.id === id ? updated : item)));
      return updated;
    },

    async archive(id: string): Promise<void> {
      const archivedAt = new Date().toISOString();
      writeActiveTargets(
        readActiveTargets().map((target) =>
          target.id === id ? { ...target, archivedAt, updatedAt: archivedAt } : target,
        ),
      );
    },

    async suggestions(): Promise<ActiveSearchTargetSuggestion[]> {
      return mockTargetSuggestions();
    },
  },

  extension: {
    async initiate(): Promise<ExtensionInitiateResponse> {
      try {
        window.localStorage.setItem(mockExtensionConnectedStorageKey, "true");
      } catch {
        // best effort
      }
      return {
        expiresAt: new Date(Date.now() + 5 * 60_000).toISOString(),
        handshakeToken: "mock-extension-handshake",
        redirectUrl: `${window.location.origin}/extension/handshake?token=mock-extension-handshake`,
      };
    },

    async getSession(): Promise<ExtensionSessionResponse> {
      let connected = false;
      try {
        connected = window.localStorage.getItem(mockExtensionConnectedStorageKey) === "true";
      } catch {
        connected = false;
      }

      return {
        connected,
        session: connected
          ? {
              id: "mock-extension-session",
              lastUsedAt: new Date().toISOString(),
              userAgent: "Liminl dev mock",
            }
          : null,
      };
    },
  },

  async getBootstrap(): Promise<AppBootstrap> {
    if (resolveMockScenario() === "failed_api") throw new Error("Mock API failure.");
    if (resolveMockScenario() === "unauthenticated") return { profile: null, session: null };
    return { profile: mockProfile(), session: mockSession };
  },

  async getOnboardingProfile(): Promise<OnboardingProfile | null> {
    return mockOnboardingProfile();
  },

  async saveOnboardingProfile(
    patch: Partial<Pick<OnboardingProfile, "experienceSummary" | "portfolioUrl" | "linkedinUrl">>,
  ): Promise<OnboardingProfile> {
    return { ...mockOnboardingProfile(), ...patch };
  },

  async getGoals(): Promise<UserGoals> {
    return { ...mockGoals(), ...readJson<Partial<UserGoals>>(mockGoalsStorageKey, {}) };
  },

  async getOutreachStats(): Promise<OutreachStats> {
    if (resolveMockScenario() === "failed_api") throw new Error("Mock API failure.");
    return {
      totalAllTime: 42,
      sentThisWeek: 5,
      sentToday: 1,
      byKind: { EXISTING_PATH: 20, DETECTED_ROLE: 15, PRIOR_INTRO: 7 },
    };
  },

  async listMyAccessCodes(): Promise<ListAccessCodesResponse> {
    return mockAccessCode();
  },

  async saveGoals(partial: Partial<UserGoals>): Promise<UserGoals> {
    const merged = {
      ...readJson<Partial<UserGoals>>(mockGoalsStorageKey, {}),
      ...partial,
    };
    writeJson(mockGoalsStorageKey, merged);
    return { ...mockGoals(), ...merged };
  },

  async getSearchCriteria(): Promise<SearchCriteria | null> {
    const stored = readMockSearchCriteria();
    if (stored) return stored;
    return searchCriteriaFromGoal(readDraft().answers.goal);
  },

  async saveSearchCriteria(input: SearchCriteriaInput): Promise<SaveSearchCriteriaResult> {
    const next: SearchCriteria = {
      id: "mock-search-criteria",
      criteriaVersion: (readMockSearchCriteria()?.criteriaVersion ?? 0) + 1,
      ...input,
    };
    writeMockSearchCriteria(next);
    return { searchCriteriaId: next.id, targetIds: [] };
  },

  async signIn(_input: SignInInput): Promise<AppSession> {
    return mockSession;
  },

  async signUp(_input: SignUpInput): Promise<AppSession> {
    // New signup always starts onboarding in the incomplete mock scenario.
    if (resolveMockScenario() === "onboarding_incomplete") {
      try {
        window.localStorage.removeItem(storageKey);
        window.localStorage.removeItem(mockGoalsStorageKey);
        window.localStorage.removeItem(mockSearchCriteriaStorageKey);
        window.sessionStorage.removeItem("liminl.chatOnboarding.progress.v1");
        window.sessionStorage.removeItem("liminl.chatOnboarding.progress.v2");
        window.sessionStorage.removeItem("liminl.chatOnboarding.googleOAuthHandoff.v2");
        window.localStorage.removeItem("liminl.mockGoogleConnected");
      } catch {
        // best effort
      }
    }
    return mockSession;
  },

  async signInWithGoogle(_input: GoogleLoginInput): Promise<GoogleLoginResult> {
    const incomplete = resolveMockScenario() === "onboarding_incomplete";
    if (incomplete) {
      try {
        window.localStorage.removeItem(storageKey);
        window.localStorage.removeItem(mockGoalsStorageKey);
        window.localStorage.removeItem(mockSearchCriteriaStorageKey);
        window.sessionStorage.removeItem("liminl.chatOnboarding.progress.v1");
        window.sessionStorage.removeItem("liminl.chatOnboarding.progress.v2");
        window.sessionStorage.removeItem("liminl.chatOnboarding.googleOAuthHandoff.v2");
      } catch {
        // best effort
      }
    }
    return {
      session: mockSession,
      hasCompletedOnboarding: !incomplete && mockOnboardingStatus() === "complete",
    };
  },

  async signOut(): Promise<void> {
    try {
      window.localStorage.removeItem(storageKey);
      window.localStorage.removeItem(mockGoalsStorageKey);
      window.localStorage.removeItem(mockSearchCriteriaStorageKey);
    } catch {
      // best effort
    }
  },

  async startGoogleOAuth(returnTo?: string): Promise<{ authorizationUrl: string }> {
    try {
      window.localStorage.setItem("liminl.mockGoogleConnected", "true");
    } catch {
      // best effort
    }
    return { authorizationUrl: returnTo ?? window.location.href };
  },

  async getGoogleOAuthStatus() {
    let connected = false;
    try {
      connected = window.localStorage.getItem("liminl.mockGoogleConnected") === "true";
    } catch {
      connected = false;
    }
    const nowIso = new Date().toISOString();
    return {
      connected,
      googleEmail: connected ? mockSession.user.email : null,
      // Phase 1 scopes only - Gmail is intentionally excluded.
      scopesGranted: connected ? ["contacts", "calendar"] : [],
      status: connected ? ("ACTIVE" as const) : undefined,
      connectedAt: connected ? nowIso : null,
      lastSyncedAt: connected ? { calendar: nowIso } : undefined,
    };
  },

  async disconnectGoogleOAuth(): Promise<{ disconnected: boolean }> {
    try {
      window.localStorage.removeItem("liminl.mockGoogleConnected");
    } catch {
      // best effort
    }
    return { disconnected: true };
  },

  async getCalendarEvents(limit = 5): Promise<CalendarEventsResult> {
    let connected = false;
    try {
      connected = window.localStorage.getItem("liminl.mockGoogleConnected") === "true";
    } catch {
      connected = false;
    }
    if (!connected) return { events: [] };

    const now = Date.now();
    const hour = 60 * 60 * 1000;
    const day = 24 * hour;
    const sample: CalendarEventsResult["events"] = [
      {
        id: "mock-event-1",
        title: "Coffee chat with Priya Raman",
        start: new Date(now + 2 * hour).toISOString(),
        end: new Date(now + 3 * hour).toISOString(),
        attendeeCount: 2,
        location: "Blue Bottle, SoMa",
        allDay: false,
        htmlLink: "https://www.google.com/calendar/event?eid=mock-event-1",
        joinLink: null,
        status: "confirmed",
        isRecurring: false,
        isOrganizer: false,
        acceptedCount: 2,
        pendingCount: 0,
        calendarId: "primary",
        calendarSummary: "Primary",
        calendarPrimary: true,
      },
      {
        id: "mock-event-2",
        title: "Product team weekly sync",
        start: new Date(now + day + hour).toISOString(),
        end: new Date(now + day + 2 * hour).toISOString(),
        attendeeCount: 8,
        location: null,
        allDay: false,
        htmlLink: "https://www.google.com/calendar/event?eid=mock-event-2",
        joinLink: "https://meet.google.com/mock-weekly-sync",
        status: "confirmed",
        isRecurring: true,
        isOrganizer: true,
        acceptedCount: 5,
        pendingCount: 3,
        calendarId: "primary",
        calendarSummary: "Primary",
        calendarPrimary: true,
      },
      {
        id: "mock-event-3",
        title: "Intro call - Acme recruiting",
        start: new Date(now + 2 * day).toISOString(),
        end: new Date(now + 2 * day + 0.5 * hour).toISOString(),
        attendeeCount: 3,
        location: null,
        allDay: false,
        htmlLink: "https://www.google.com/calendar/event?eid=mock-event-3",
        joinLink: "https://meet.google.com/mock-acme-intro",
        status: "tentative",
        isRecurring: false,
        isOrganizer: false,
        acceptedCount: 2,
        pendingCount: 1,
        calendarId: "work-shared",
        calendarSummary: "Work Shared",
        calendarPrimary: false,
      },
      {
        id: "mock-event-4",
        title: "Design Systems Roundtable",
        start: new Date(now + 3 * day).toISOString(),
        end: new Date(now + 3 * day + hour).toISOString(),
        attendeeCount: 5,
        location: "Virtual",
        allDay: false,
        htmlLink: "https://www.google.com/calendar/event?eid=mock-event-4",
        joinLink: "https://meet.google.com/mock-roundtable",
        status: "confirmed",
        isRecurring: false,
        isOrganizer: false,
        acceptedCount: 4,
        pendingCount: 1,
        calendarId: "work-shared",
        calendarSummary: "Work Shared",
        calendarPrimary: false,
      },
      {
        id: "mock-event-5",
        title: "Dinner with Engineering Lead",
        start: new Date(now + 4 * day).toISOString(),
        end: new Date(now + 4 * day + 2 * hour).toISOString(),
        attendeeCount: 2,
        location: "Stones Throw, SF",
        allDay: false,
        htmlLink: "https://www.google.com/calendar/event?eid=mock-event-5",
        joinLink: null,
        status: "confirmed",
        isRecurring: false,
        isOrganizer: true,
        acceptedCount: 2,
        pendingCount: 0,
        calendarId: "primary",
        calendarSummary: "Primary",
        calendarPrimary: true,
      },
      {
        id: "mock-event-6",
        title: "Coffee chat with Alex Rivera",
        start: new Date(now + 5 * day).toISOString(),
        end: new Date(now + 5 * day + hour).toISOString(),
        attendeeCount: 2,
        location: "Blue Bottle, SF",
        allDay: false,
        htmlLink: "https://www.google.com/calendar/event?eid=mock-event-6",
        joinLink: null,
        status: "confirmed",
        isRecurring: false,
        isOrganizer: false,
        acceptedCount: 1,
        pendingCount: 1,
        calendarId: "primary",
        calendarSummary: "Primary",
        calendarPrimary: true,
      },
      {
        id: "mock-event-7",
        title: "Sync with Product Operations",
        start: new Date(now + 6 * day).toISOString(),
        end: new Date(now + 6 * day + hour).toISOString(),
        attendeeCount: 4,
        location: null,
        allDay: false,
        htmlLink: "https://www.google.com/calendar/event?eid=mock-event-7",
        joinLink: "https://meet.google.com/mock-product-ops",
        status: "confirmed",
        isRecurring: true,
        isOrganizer: false,
        acceptedCount: 3,
        pendingCount: 1,
        calendarId: "team-ops",
        calendarSummary: "Team Ops",
        calendarPrimary: false,
      },
      {
        id: "mock-event-8",
        title: "AI Founders & Investors Mixer",
        start: new Date(now + 7 * day).toISOString(),
        end: new Date(now + 7 * day + 2 * hour).toISOString(),
        attendeeCount: 6,
        location: "Manhattan, NY",
        allDay: false,
        htmlLink: "https://www.google.com/calendar/event?eid=mock-event-8",
        joinLink: null,
        status: "confirmed",
        isRecurring: false,
        isOrganizer: false,
        acceptedCount: 4,
        pendingCount: 2,
        calendarId: "primary",
        calendarSummary: "Primary",
        calendarPrimary: true,
      },
      {
        id: "mock-event-9",
        title: "Strategy alignment session",
        start: new Date(now + 8 * day).toISOString(),
        end: new Date(now + 8 * day + hour).toISOString(),
        attendeeCount: 5,
        location: "Office",
        allDay: false,
        htmlLink: "https://www.google.com/calendar/event?eid=mock-event-9",
        joinLink: "https://meet.google.com/mock-strategy",
        status: "confirmed",
        isRecurring: false,
        isOrganizer: true,
        acceptedCount: 3,
        pendingCount: 2,
        calendarId: "primary",
        calendarSummary: "Primary",
        calendarPrimary: true,
      },
      {
        id: "mock-event-10",
        title: "Coffee chat with Maya Chen",
        start: new Date(now + 9 * day).toISOString(),
        end: new Date(now + 9 * day + hour).toISOString(),
        attendeeCount: 2,
        location: "Blue Bottle, SF",
        allDay: false,
        htmlLink: "https://www.google.com/calendar/event?eid=mock-event-10",
        joinLink: null,
        status: "tentative",
        isRecurring: false,
        isOrganizer: false,
        acceptedCount: 1,
        pendingCount: 1,
        calendarId: "primary",
        calendarSummary: "Primary",
        calendarPrimary: true,
      },
    ];
    return { events: sample.slice(0, Math.max(5, limit)) };
  },

  async getGoogleCalendars(): Promise<GoogleCalendarsResult> {
    return {
      calendars: [
        {
          id: "primary",
          summary: "Primary",
          primary: true,
          selected: true,
          accessRole: "owner",
          backgroundColor: null,
          foregroundColor: null,
        },
        {
          id: "work-shared",
          summary: "Work Shared",
          primary: false,
          selected: true,
          accessRole: "reader",
          backgroundColor: null,
          foregroundColor: null,
        },
        {
          id: "team-ops",
          summary: "Team Ops",
          primary: false,
          selected: true,
          accessRole: "writer",
          backgroundColor: null,
          foregroundColor: null,
        },
      ],
    };
  },

  async getEventsAiSummary(): Promise<EventsAiSummaryResult> {
    await new Promise((r) => window.setTimeout(r, 400));
    return {
      enabled: true,
      summary: {
        text: "You have several networking moments on the calendar this week, including a coffee chat and a shared team event. Lean into the conversations closest to your target roles - those are your highest-leverage touchpoints right now.",
        generatedBy: "ai",
        updatedAt: new Date().toISOString(),
      },
    };
  },

  async getSuggestedEvents(): Promise<SuggestedEventsResult> {
    await new Promise((r) => window.setTimeout(r, 250));
    const base = Date.now();
    const day = 24 * 60 * 60 * 1000;
    const mk = (i: number, title: string, venue: string) => ({
      id: `mock-suggested-${i}`,
      title,
      dateLabel: new Date(base + i * day).toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
      }),
      startAt: new Date(base + i * day).toISOString().slice(0, 10),
      venue,
      address: `${venue}, San Francisco`,
      link: `https://example.com/events/${i}`,
      thumbnail: null,
    });
    return {
      enabled: true,
      location: "San Francisco",
      query: "professional networking events in San Francisco",
      visibleCount: 3,
      items: [
        mk(1, "Women in Product Meetup", "Sightglass Coffee"),
        mk(2, "Startup Founder Office Hours", "RocketSpace"),
        mk(3, "AI Builders Happy Hour", "The Battery"),
        mk(4, "Design Systems Salon", "Figma HQ"),
        mk(5, "YC Alumni Coffee Chat", "Cafe Reveille"),
        mk(6, "Growth Marketing Mixer", "WeWork"),
        mk(7, "Open Source Contrib Night", "GitHub"),
        mk(8, "Career Switcher Panel", "General Assembly"),
        mk(9, "Angel Investor Office Hours", "SOSV"),
        mk(10, "Community Builders Dinner", "Foreign Cinema"),
      ],
    };
  },

  onboarding: {
    async getDraft(): Promise<GetOnboardingDraftResult> {
      const draft = readDraft();
      return {
        draft,
        options: mockOptions,
        stepStatuses: stepStatuses(draft),
      };
    },

    async saveStep(input: SaveOnboardingStepInput): Promise<SaveOnboardingStepResult> {
      const current = readDraft();
      const answers: OnboardingAnswers = { ...current.answers };
      answers.searchIntent = input.payload.searchIntent ?? answers.searchIntent;

      switch (input.stepId) {
        case "resume":
          answers.resume = input.payload.resume;
          break;
        case "network":
          answers.networkSources = input.payload.networkSources;
          answers.manualContacts = input.payload.manualContacts;
          break;
        case "goal":
          answers.goal = input.payload.goal;
          answers.careerQuiz = input.payload.careerQuiz ?? answers.careerQuiz;
          break;
        case "review":
          answers.review = input.payload.review;
          if ("phone" in input.payload) answers.phone = input.payload.phone ?? null;
          break;
      }

      return saveAnswers(answers, current.revision + 1);
    },

    async complete(_input: CompleteOnboardingInput): Promise<CompleteOnboardingResult> {
      const current = readDraft();
      const completedDraft: OnboardingDraft = {
        ...current,
        completedSteps: [...onboardingStepOrder],
        currentStep: "review",
        status: "complete",
        updatedAt: new Date().toISOString(),
      };
      writeDraft(completedDraft);

      // Mirror real onboarding complete: persist goals + SearchCriteria so Profile
      // / jobs scoring can read the structured prefs captured in chat.
      writeJson(mockGoalsStorageKey, mockGoals());
      const criteria = searchCriteriaFromGoal(completedDraft.answers.goal);
      if (criteria) writeMockSearchCriteria(criteria);

      return {
        answers: completedDraft.answers,
        completedAt: completedDraft.updatedAt,
        dashboardSeed: {
          mapHeadline: "My Action Plan",
          networkInputCount: completedDraft.answers.manualContacts.length,
          nextActions: ["Review your warmest paths", "Draft the first intro", "Keep follow-ups moving"],
          primaryGoal: completedDraft.answers.goal?.headline ?? "",
          resumeSignalCount: completedDraft.answers.resume?.parsedSignals.length ?? 0,
          targetCount: completedDraft.answers.goal?.targets.length ?? 0,
        },
        draft: completedDraft,
        profileSeed: {
          connectedSourceKinds: completedDraft.answers.networkSources
            .filter((source) => source.connected)
            .map((source) => source.kind),
          goalHeadline: completedDraft.answers.goal?.headline ?? "",
          outreachComfort: completedDraft.answers.relationshipComfort.outreachComfort,
          resumeSignals: completedDraft.answers.resume?.parsedSignals ?? [],
          selectedSourceKinds: completedDraft.answers.networkSources
            .filter((source) => source.intent !== "skip")
            .map((source) => source.kind),
          targetLabels: completedDraft.answers.goal?.targets.map((target) => target.label) ?? [],
        },
      };
    },

    async parseCv(): Promise<ParseCvResult> {
      return mockParseCvResult();
    },

    async parseCvFile(): Promise<ParseCvResult> {
      return mockParseCvResult();
    },

    async extractLinkedIn(): Promise<LinkedInExtractionResult> {
      return {
        confidenceByField: {},
        currentCompany: "Northwind Labs",
        currentRole: "Senior Product Manager",
        education: ["UC Berkeley - B.S. Computer Science"],
        extractionStatus: "ok",
        fullName: "Alex Rivera",
        headline: "Product leader for 0→1 bets",
        location: "San Francisco, CA",
        pastExperiences: [
          { company: "Acme", role: "Product Manager" },
          { company: "Globex", role: "Associate PM" },
        ],
        profileUrl: "https://www.linkedin.com/in/mock",
        skills: ["Product strategy", "Roadmapping", "Analytics"],
        summary: "Product leader focused on early-stage growth and platform bets.",
      };
    },

    async suggestValues() {
      return { values: ["High agency", "Warm relationships", "Clear communication"] };
    },

    async suggestFocusAreas() {
      return { focusAreas: ["Climate tech", "AI tools", "Developer tools"] };
    },

    async suggestRolesIndustries() {
      return {
        industries: ["Climate tech", "AI tools", "Fintech"],
        roles: ["Product Operations", "Product Manager", "Chief of Staff"],
      };
    },

    async generateQuiz(): Promise<GenerateQuizResult> {
      return {
        pathwayPreview: [
          {
            description: "Use relationship-driven execution to support product teams.",
            id: "mock-product-ops",
            title: "Product Operations",
          },
          {
            description: "Blend market context, operators, and warm routes into focused moves.",
            id: "mock-chief-of-staff",
            title: "Chief of Staff",
          },
        ],
        questions: [
          {
            id: "mock-q1",
            options: [
              { id: "mock-q1-a", label: "Fast-moving ones where you react in the moment", scoreKey: "operator" },
              { id: "mock-q1-b", label: "Complex ones that take deep analysis", scoreKey: "analyst" },
              { id: "mock-q1-c", label: "People-shaped ones that depend on relationships", scoreKey: "connector" },
              { id: "mock-q1-d", label: "Ambiguous ones where nobody knows the answer", scoreKey: "builder" },
            ],
            text: "What kind of problem do you most enjoy solving?",
          },
        ],
        source: "generated",
      };
    },

    async saveChatMemory(): Promise<{ saved: boolean }> {
      return { saved: true };
    },

    // Mock: client bridges prior answers onto static step copy (see ChatOnboarding).
    async personalizedQuestion(): Promise<PersonalizedQuestionResult> {
      return { source: "fallback", question: "" };
    },
  },
};
